import type { CityConfig } from "@/pages/city-template/types";

export const newYorkCityConfig: CityConfig = {
  city: "New York City",
  state: "New York",
  stateAbbr: "NY",
  slug: "new-york-city",
  route: "/cities/new-york-city",
  title: "Emergency & Expedited Passport NYC | Same Day Passport New York City",
  metaDescription:
    "Emergency passport NYC — same day, 24-hour, and 3-5 day expedited passport processing for all five boroughs. Fast Passport Center serves Manhattan, Brooklyn, Queens, The Bronx, and Staten Island. Avoid the 8-11 week wait.",
  h1: "Emergency & Expedited Passport Services in New York City",
  heroSubtitle:
    "New York City never sleeps — and neither should your passport service. Whether you're flying out of JFK, LGA, or Newark, Fast Passport Center delivers emergency passports in as little as 24 hours and expedited processing in 3-5 days for all five boroughs.",
  heroImageUrl:
    "https://readdy.ai/api/search-image?query=Aerial%20view%20of%20Lower%20Manhattan%20skyline%20with%20One%20World%20Trade%20Center%20and%20Brooklyn%20Bridge%20at%20golden%20hour%2C%20dramatic%20New%20York%20City%20skyline%2C%20warm%20sunset%20lighting%20reflecting%20off%20glass%20skyscrapers%2C%20modern%20travel%20photography%2C%20clean%20composition%2C%20no%20text%20overlay%2C%20high%20contrast%20urban%20landscape%20with%20Hudson%20River%20in%20foreground&width=1600&height=700&seq=ny-nyc-hero-001&orientation=landscape",
  heroImageAlt: "New York City skyline at golden hour with Lower Manhattan and Brooklyn Bridge",
  geoPosition: "40.7128; -74.0060",
  geoPlacename: "New York City, New York, United States",
  lastModified: "2026-05-31",
  regionalAgency: {
    name: "New York Passport Agency",
    address: "376 Hudson Street, New York, NY 10014",
    distance: "In Manhattan",
  },
  nearbyCitiesZips: [
    { name: "Manhattan", zips: "10001-10007, 10009-10014, 10016-10022, 10023-10025, 10026-10028, 10029-10033, 10036-10039, 10040-10045, 10065, 10075, 10280, 10282" },
    { name: "Brooklyn", zips: "11201-11203, 11204-11210, 11211-11215, 11216-11220, 11221-11226, 11228-11232, 11233-11239, 11249, 11251" },
    { name: "Queens", zips: "11101-11106, 11354-11358, 11360-11368, 11369-11375, 11377-11379, 11385, 11411-11423, 11426-11436, 11691-11697" },
    { name: "The Bronx", zips: "10451-10475, 10499" },
    { name: "Staten Island", zips: "10301-10314" },
    { name: "Jersey City", zips: "07302-07311, 07395, 07399" },
    { name: "Hoboken", zips: "07030" },
    { name: "Yonkers", zips: "10701-10705, 10710" },
    { name: "Mount Vernon", zips: "10550-10553" },
    { name: "New Rochelle", zips: "10801-10805" },
    { name: "White Plains", zips: "10601-10607, 10610" },
    { name: "Albany", zips: "12201-12212, 12214, 12220, 12222-12226, 12228, 12230, 12231, 12232, 12233, 12234, 12235, 12236, 12237, 12238, 12239, 12240, 12241, 12242, 12243, 12244, 12245, 12246, 12247, 12248, 12249, 12250, 12252, 12255, 12256, 12257, 12260, 12261, 12262, 12288" },
    { name: "Buffalo", zips: "14201-14204, 14206-14210, 14211-14214, 14215, 14216, 14217, 14218, 14219, 14220, 14221, 14222, 14223, 14224, 14225, 14226, 14227, 14228, 14231, 14233, 14240, 14241, 14260, 14261, 14263, 14264, 14265, 14267, 14269, 14270, 14272, 14273, 14276, 14280" },
    { name: "Rochester", zips: "14602, 14603, 14604, 14605, 14606, 14607, 14608, 14609, 14610, 14611, 14612, 14613, 14614, 14615, 14616, 14617, 14618, 14619, 14620, 14621, 14622, 14623, 14624, 14625, 14626, 14627, 14642, 14650, 14652, 14653, 14692" },
    { name: "Syracuse", zips: "13201-13210, 13211, 13212, 13214, 13215, 13217, 13218, 13219, 13220, 13224, 13225, 13235, 13244, 13250, 13251, 13252, 13261, 13290" },
  ],
  benefits: [
    {
      icon: "ri-time-line",
      title: "As Fast as 24 Hours",
      desc: "Emergency passport processing for NYC travelers with urgent departures from JFK, LGA, or Newark — same day service available for documented emergencies.",
    },
    {
      icon: "ri-shield-check-line",
      title: "Registered Courier",
      desc: "U.S. Department of State registered passport courier with direct access to the New York Passport Agency.",
    },
    {
      icon: "ri-customer-service-2-line",
      title: "Real Human Support",
      desc: "Dedicated agents guide you through every step — no chatbots, no automated runaround. Available for NYC residents 7 days a week.",
    },
    {
      icon: "ri-map-pin-line",
      title: "All 5 Boroughs",
      desc: "Manhattan, Brooklyn, Queens, The Bronx, and Staten Island — we serve every neighborhood in New York City.",
    },
  ],
  testimonials: [
    {
      name: "David K.",
      location: "Upper East Side, Manhattan",
      rating: 5,
      text: "Had a business trip to London in 3 days. Fast Passport Center got my emergency passport processed in 24 hours. Submitted at their office near Grand Central and had it back the next day.",
      service: "Emergency Passport",
    },
    {
      name: "Sarah M.",
      location: "Park Slope, Brooklyn",
      rating: 5,
      text: "Renewed my expired passport through Fast Passport Center. Chose the 5-day option and had it back in 4. They caught a photo sizing issue before submission.",
      service: "Passport Renewal",
    },
    {
      name: "Jennifer T.",
      location: "Astoria, Queens",
      rating: 5,
      text: "First-time passport for my daughter. The agent explained the two-parent rule and helped us get the consent forms right. Processed in 5 days.",
      service: "Child Passport",
    },
  ],
  facilities: [
    {
      id: "nyc-1",
      name: "James A. Farley Building Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "421 8th Avenue",
      city: "New York",
      state: "NY",
      zip: "10199",
      phone: "212-330-3296",
      hours: "Mon-Fri 8:00 AM - 6:00 PM, Sat 9:00 AM - 3:00 PM",
      distance: "Midtown Manhattan",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major NYC passport acceptance facility. First-time applicants and those not eligible for mail-in renewal must apply in person. Located near Penn Station.",
      icon: "ri-building-2-line",
    },
    {
      id: "nyc-2",
      name: "Brooklyn Main Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "271 Cadman Plaza East",
      city: "Brooklyn",
      state: "NY",
      zip: "11201",
      phone: "718-858-8329",
      hours: "Mon-Fri 8:00 AM - 5:00 PM, Sat 9:00 AM - 1:00 PM",
      distance: "Downtown Brooklyn",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport acceptance services available by appointment. Convenient for Brooklyn residents near Borough Hall.",
      icon: "ri-building-2-line",
    },
    {
      id: "nyc-3",
      name: "Queens Main Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "3905 Main Street",
      city: "Flushing",
      state: "NY",
      zip: "11354",
      phone: "718-321-8750",
      hours: "Mon-Fri 8:00 AM - 5:00 PM, Sat 9:00 AM - 1:00 PM",
      distance: "Flushing, Queens",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport acceptance services available. Serving Queens residents and those near the 7 train and LIRR.",
      icon: "ri-building-2-line",
    },
  ],
  faqs: [
    {
      q: "How fast can I get an emergency passport in New York City?",
      a: "Fast Passport Center offers same-day emergency passport processing in as little as 24 hours for NYC residents with documented life-or-death emergencies. For urgent travel within 2 weeks, our 3-5 day expedited service is the most popular option. Standard processing through the post office takes 8-11 weeks.",
    },
    {
      q: "Where is the closest passport acceptance facility in New York City?",
      a: "The James A. Farley Building Post Office at 421 8th Avenue (Midtown Manhattan) is the major NYC passport acceptance facility. The Brooklyn Main Post Office at 271 Cadman Plaza East and Queens Main Post Office at 3905 Main Street in Flushing are also convenient options. First-time applicants and those not eligible for mail-in renewal must apply in person at these facilities. Fast Passport Center guides you through the acceptance clerk process.",
    },
    {
      q: "Can I get same-day passport photos in NYC?",
      a: "Yes — passport photos are widely available at CVS Pharmacy, Walgreens, Duane Reade, UPS Store, and FedEx Office locations throughout all five boroughs. We also offer photo review as part of our service to ensure your 2x2 photos meet all State Department requirements before submission.",
    },
    {
      q: "How much does an expedited passport cost in New York City?",
      a: "Government fees are set by the State Department and vary based on passport type (book vs card, large book option). Fast Passport Center's service fees depend on processing speed: 10-day Smart Saver, 3-5 day Expedited, or 24-hour Emergency. Call 877-253-0084 for a personalized quote based on your NYC passport needs.",
    },
    {
      q: "Do I need to visit the New York Passport Agency in person?",
      a: "No — if you use Fast Passport Center, you do not need to visit the New York Passport Agency at 376 Hudson Street in person at all. As a registered courier, we handle the agency submission for you. The only reason to visit the agency directly is for a documented life-or-death emergency, and appointments are extremely difficult to obtain.",
    },
    {
      q: "What is the fastest way to renew a passport in New York City?",
      a: "The fastest way to renew a passport in NYC is through Fast Passport Center's 3-5 day expedited service. If your most recent passport was issued when you were 16 or older, within the last 15 years, and is undamaged, you may qualify for renewal without an acceptance clerk visit. For even faster renewal, our 24-hour emergency service is available.",
    },
    {
      q: "Can I get a child passport expedited in New York City?",
      a: "Yes — Fast Passport Center expedites child passports for minors under 16 in all five boroughs. Both parents must appear with the child or provide notarized consent (Form DS-3053). We review all parental consent documentation before submission to avoid delays.",
    },
    {
      q: "Does Fast Passport Center have an office in New York City?",
      a: "Fast Passport Center operates as a remote courier service — we do not have a physical office in NYC. We serve all five boroughs through our secure overnight shipping and agency submission process. Our headquarters are in Washington, DC.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — All NYC Boroughs", address: "various CVS locations across Manhattan, Brooklyn, Queens, The Bronx, and Staten Island", hours: "Mon-Sun, hours vary by location" },
    { name: "Walgreens / Duane Reade — NYC", address: "various Walgreens and Duane Reade locations throughout all five boroughs", hours: "Mon-Sun, hours vary by location" },
    { name: "UPS Store — Manhattan & Brooklyn", address: "various UPS Store locations in Midtown, Downtown, and Brooklyn neighborhoods", hours: "Mon-Fri 9:00 AM - 7:00 PM, Sat 10:00 AM - 4:00 PM" },
    { name: "FedEx Office — NYC", address: "various FedEx Office locations across Manhattan, Brooklyn, and Queens", hours: "Mon-Fri 8:00 AM - 9:00 PM, Sat-Sun 10:00 AM - 6:00 PM" },
    { name: "Photo Studios — Midtown Manhattan", address: "various professional photo studios near Times Square and Penn Station", hours: "Mon-Sun 9:00 AM - 8:00 PM" },
  ],
  dmvLocations: [
    {
      name: "NYS DMV — Lower Manhattan",
      address: "11 Greenwich Street, New York, NY 10004",
      hours: "Mon-Fri 8:30 AM - 4:00 PM",
      note: "Manhattan DMV office. Accepts walk-ins but appointments are strongly recommended. REAL ID and driver's license services.",
      featured: true,
    },
    {
      name: "NYS DMV — Brooklyn",
      address: "625 Atlantic Avenue, Brooklyn, NY 11217",
      hours: "Mon-Fri 8:30 AM - 4:00 PM",
      note: "Brooklyn DMV office for license renewals and REAL ID applications. Convenient for Brooklyn residents.",
      featured: false,
    },
    {
      name: "NYS DMV — Queens",
      address: "168-46 91st Avenue, Jamaica, NY 11432",
      hours: "Mon-Fri 8:30 AM - 4:00 PM",
      note: "Queens DMV office. Serving Queens and Long Island residents.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "JFK",
      name: "John F. Kennedy International Airport",
      distance: "In Queens, NY",
      address: "Queens, NY 11430",
      note: "NYC's primary international airport with direct flights to Europe, Asia, Africa, and worldwide destinations. Most NYC travelers use JFK for international travel.",
      flightUrl: "https://www.jfkairport.com/",
      flightLabel: "JFK Airport",
      featured: true,
    },
    {
      code: "LGA",
      name: "LaGuardia Airport",
      distance: "In Queens, NY",
      address: "Queens, NY 11371",
      note: "Domestic airport serving major U.S. cities. Recently renovated with modern terminals. Closer to Manhattan than JFK.",
      flightUrl: "https://www.laguardiaairport.com/",
      flightLabel: "LaGuardia Airport",
      featured: false,
    },
    {
      code: "EWR",
      name: "Newark Liberty International Airport",
      distance: "In Newark, NJ (near NYC)",
      address: "3 Brewster Rd, Newark, NJ 07114",
      note: "Major international airport in New Jersey serving the NYC metro area. Direct flights to Europe, Asia, and worldwide. Accessible via NJ Transit and PATH.",
      flightUrl: "https://www.newarkairport.com/",
      flightLabel: "Newark Airport",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-passport-line",
      title: "Emergency Passport",
      description: "For NYC travelers with urgent departures within 24 hours to 2 weeks. Direct New York Passport Agency submission with dedicated courier handling.",
      bullets: ["Processing as fast as 24 hours", "Dedicated agent assigned to your case", "Daily status updates"],
      badge: "Fastest",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Fast renewal for NYC residents with a valid passport issued within the last 15 years at age 16 or older.",
      bullets: ["3-5 day or 10-day options available", "No acceptance clerk visit required", "Secure overnight document return"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-user-add-line",
      title: "New Passport",
      description: "First-time passport application assistance for NYC residents with step-by-step guidance through the acceptance clerk process.",
      bullets: ["Complete application review before submission", "Acceptance clerk appointment guidance", "3-5 day or 10-day processing options"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alarm-warning-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement service for NYC residents who have lost their passport or had it stolen.",
      bullets: ["DS-64 form guidance included", "Replacement in 3-5 days or 10 days", "Acceptance clerk visit required"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 in NYC, with parental consent guidance and acceptance clerk support.",
      bullets: ["Parental consent documentation review", "Both parents present or notarized consent", "3-5 day or 10-day processing available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal name change for NYC residents.",
      bullets: ["Marriage certificate or court order review", "Name change in 3-5 days or 10 days", "No clerk visit for most renewals"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for NYC travelers with immediate departure needs from JFK, LGA, or Newark." },
    { name: "Passport Renewal", description: "Fast passport renewal for NYC residents with a recent valid passport." },
    { name: "New Passport", description: "First-time passport application assistance for NYC residents." },
    { name: "Lost or Stolen Passport", description: "Passport replacement for NYC residents who have lost their passport." },
    { name: "Child Passport", description: "Passport services for minors under 16 in New York City." },
    { name: "Name Change", description: "Passport name change service after marriage, divorce, or legal name change in NYC." },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Photos must be 2\"x2\" with a plain white or off-white background. Many NYC travelers get rejected at the acceptance clerk because their photos don't meet strict State Department specs.",
    },
    {
      icon: "ri-file-list-3-line",
      title: "Missing or Incorrect Forms",
      description: "Using the wrong form (DS-11 vs DS-82) or missing required fields can cause weeks of delays. We review every form before submission.",
    },
    {
      icon: "ri-calendar-line",
      title: "Waiting Too Long to Start",
      description: "Standard processing takes 8-11 weeks. Many NYC travelers wait until the last minute, leaving no buffer for problems.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Incorrect Payment Amount",
      description: "Government fees change frequently. Using an outdated check or wrong amount will cause your application to be returned.",
    },
    {
      icon: "ri-shield-line",
      title: "Damaged or Expired ID",
      description: "Your driver's license or state ID must be valid and unexpired. Many applicants don't realize their ID is about to expire until it's too late.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For child passports, both parents must appear or provide notarized consent. Missing documentation is the #1 reason child applications are delayed.",
    },
  ],
  steps: [
    {
      number: "01",
      icon: "ri-file-list-3-line",
      title: "Complete Your Application",
      description: "Fill out your passport application online or over the phone with our NYC team. We review everything for accuracy.",
    },
    {
      number: "02",
      icon: "ri-upload-cloud-line",
      title: "Send Us Your Documents",
      description: "Ship your documents via overnight courier to our secure processing center. We confirm receipt and begin review immediately.",
    },
    {
      number: "03",
      icon: "ri-search-line",
      title: "We Review Everything",
      description: "Our agents verify your forms, photos, and supporting documents meet all State Department requirements — no surprises.",
    },
    {
      number: "04",
      icon: "ri-shield-check-line",
      title: "Courier Submission",
      description: "As a registered U.S. Department of State courier, we submit your application directly to the New York Passport Agency.",
    },
    {
      number: "05",
      icon: "ri-passport-line",
      title: "Receive Your Passport",
      description: "Your passport is delivered securely to your NYC address. We provide tracking and daily status updates throughout.",
    },
  ],
  processingOptions: [
    {
      speed: "8-11",
      unit: "weeks",
      label: "Standard Processing",
      tag: "Post Office",
      tagColor: "bg-gray-200 text-gray-600",
      features: [
        "Mail-in application",
        "No expedited handling",
        "No dedicated agent",
        "No phone or email contact for your case",
        "Updates only by physical mail letter",
      ],
      highlight: false,
      muted: true,
    },
    {
      speed: "10",
      unit: "business days",
      label: "Smart Saver",
      tag: "Fast Passport Center",
      tagColor: "bg-green-100 text-green-700",
      features: [
        "Secure overnight shipping included",
        "Document review before submission",
        "Dedicated agent assigned",
        "Daily status updates",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "3-5",
      unit: "business days",
      label: "Expedited Processing",
      tag: "Recommended",
      tagColor: "bg-[#1A365D] text-white",
      features: [
        "Secure overnight shipping included",
        "Document review before submission",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "24",
      unit: "hours",
      label: "Emergency Processing",
      tag: "Life or Death",
      tagColor: "bg-red-100 text-red-600",
      features: [
        "For documented life-or-death emergencies only",
        "Requires proof of emergency travel",
        "Strictly for documented life-or-death emergencies",
      ],
      highlight: false,
      emergency: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Must book weeks in advance", fpc: "No appointment needed — we handle agency access" },
    { feature: "Processing Speed", agency: "1-2 business days (if you get an appointment)", fpc: "As fast as 3-5 days or 24 hours for emergencies" },
    { feature: "Document Review", agency: "No pre-check — one mistake means restart", fpc: "Complete review before submission — we catch errors" },
    { feature: "Travel Proof", agency: "Requires printed itinerary with confirmed travel within 2 weeks", fpc: "Flexible documentation requirements" },
    { feature: "Updates", agency: "No phone or email support — only mail updates", fpc: "Daily status updates via phone and email" },
    { feature: "Guarantee", agency: "No guarantee of appointment or same-day processing", fpc: "Reserved agency slots with predictable processing" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Buffalo", route: "/cities/buffalo" },
    { name: "Albany", route: "/cities/albany-ny" },
    { name: "Rochester", route: "/cities/rochester" },
    { name: "Syracuse", route: "/cities/syracuse" },
    { name: "Yonkers", route: "/cities/yonkers" },
    { name: "Brooklyn", route: "/cities/brooklyn" },
    { name: "Queens", route: "/cities/queens" },
    { name: "Staten Island", route: "/cities/staten-island" },
    { name: "The Bronx", route: "/cities/the-bronx" },
    { name: "Long Island", route: "/cities/long-island" },
    { name: "Binghamton", route: "/cities/binghamton" },
    { name: "White Plains", route: "/cities/white-plains" },
    { name: "Poughkeepsie", route: "/cities/poughkeepsie" },
    { name: "Newburgh", route: "/cities/newburgh" },
    { name: "New Rochelle", route: "/cities/new-rochelle" },
    { name: "Utica", route: "/cities/utica" },
    { name: "Schenectady", route: "/cities/schenectady" },
    { name: "Troy", route: "/cities/troy" },
    { name: "Saratoga Springs", route: "/cities/saratoga-springs" },
    { name: "Elmira", route: "/cities/elmira" },
    { name: "Ithaca", route: "/cities/ithaca" },
    { name: "Corning", route: "/cities/corning" },
    { name: "Jamestown", route: "/cities/jamestown" },
    { name: "Plattsburgh", route: "/cities/plattsburgh" },
    { name: "Watertown", route: "/cities/watertown" },
    { name: "Ogdensburg", route: "/cities/ogdensburg" },
    { name: "Glens Falls", route: "/cities/glens-falls" },
    { name: "Kingston", route: "/cities/kingston" },
    { name: "Middletown", route: "/cities/middletown-ny" },
    { name: "New Paltz", route: "/cities/new-paltz" },
    { name: "Mount Vernon", route: "/cities/mount-vernon" },
    { name: "Tarrytown", route: "/cities/tarrytown" },
    { name: "Beacon", route: "/cities/beacon" },
    { name: "Fishkill", route: "/cities/fishkill" },
    { name: "Rhinebeck", route: "/cities/rhinebeck" },
    { name: "Hyde Park", route: "/cities/hyde-park" },
    { name: "Red Hook", route: "/cities/red-hook" },
    { name: "New York State", route: "/states/new-york" },
  ],
  fpcOfficeNote: "Fast Passport Center serves New York City from our local office at 347 Fifth Avenue. Visit us by appointment, or use our courier pickup throughout NYC and the tri-state area.",
  fpcOfficeDistance: "347 Fifth Avenue — Midtown Manhattan",
  fpcOfficeAddress: "347 Fifth Avenue, New York, NY 10016",
  serviceAreaIntro:
    "Fast Passport Center provides expedited passport services to all five boroughs of New York City. From the Upper East Side to Park Slope, from Astoria to the South Bronx — we serve every NYC neighborhood with the same fast, reliable processing.",
  nearbyCitiesIntro:
    "We proudly serve New York City and all surrounding communities across the Tri-State area. Whether you're in Manhattan, Brooklyn, Queens, The Bronx, or Staten Island, our expedited passport services are available to you.",
  howItWorksIntro:
    "Our NYC customers follow a simple, proven process to get their passports fast. No agency visits, no waiting in lines, no guessing.",
  mistakesIntro:
    "These are the most common mistakes NYC travelers make when applying for an expedited passport. Avoiding them saves weeks of delays.",
  testimonialsIntro:
    "New York City travelers trust Fast Passport Center for their urgent passport needs. Here's what they say about our service.",
  faqIntro:
    "Common questions about emergency and expedited passport services in New York City. If you don't see your question here, call us at 877-253-0084.",
  statePageRoute: "/states/new-york",
  statePageName: "New York Passport Services",
  dmvWebsite: "https://dmv.ny.gov",
  dmvLabel: "New York DMV — Official Website",
};

export default newYorkCityConfig;